import {
  Component,
  ChangeDetectionStrategy,
  ViewChild,
  inject,
  input,
  output,
  computed
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { AuthService } from '@services/core/auth.service';
import { Request } from '../../services/requests.service';
import { BpmService } from '../../services/bpm.service';
import {
  StepApprovalModalComponent,
  StepApprovalPayload
} from '../step-approval-modal/step-approval-modal.component';

/**
 * Acciones BPM del paso actual.
 *
 * Quién puede actuar, si pide documento y qué tipo sale del status-flow
 * (BpmService). El modal pide glosa + archivo cuando el paso lo exige.
 * En DRAFT muestra "Enviar solicitud" (salida del borrador al paso inicial).
 */
@Component({
  selector: 'app-bpm',
  standalone: true,
  imports: [CommonModule, RouterLink, StepApprovalModalComponent],
  templateUrl: './bpm.component.html',
  styleUrls: ['./bpm.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class BpmComponent {
  private bpmService = inject(BpmService);
  private authService = inject(AuthService);

  /** Solicitud en revisión. */
  request = input<Request | null>(null);
  /** True mientras corre approve / reject / upload. */
  isProcessing = input(false);
  /**
   * Bloqueo externo (ej. evidencias observadas).
   * Si hay texto, se muestra aviso y se deshabilita aprobar.
   */
  blockedReason = input<string | null>(null);
  /** Mostrar botón Rechazar (default true si canApprove; en DRAFT se oculta). */
  showReject = input(true);
  /** Wizard (add-party / add-claim): quien arma el DRAFT puede enviarlo, sin requiredRole del paso inicial. */
  allowDraftSubmit = input(false);
  /** Mostrar Eliminar junto a Enviar (borrador del wizard). */
  showDelete = input(false);
  /** Ocultar botones (p. ej. móvil: Enviar vive en el footer). Mantiene alerts. */
  hideButtons = input(false);

  /** Aprobar / enviar draft sin modal (paso sin documento ni pago). */
  approve = output<void>();
  /** Rechazar. */
  reject = output<void>();
  /** Eliminar el trámite en borrador. */
  deleteRequest = output<void>();
  /** Confirmación del modal (documento / pago). */
  confirmed = output<StepApprovalPayload>();

  @ViewChild(StepApprovalModalComponent) private approvalModal!: StepApprovalModalComponent;

  private role = computed(() => {
    const roleObj = this.authService.sharingRole() as any;
    return (roleObj?.slug ?? roleObj?.name ?? 'VISUALIZADOR').toUpperCase();
  });

  isDraft = computed(() => this.bpmService.isDraft(this.request()));

  sessionHold = computed(() => this.request()?.sessionHold?.uuid ? this.request()!.sessionHold! : null);

  canApprove = computed(() => !this.sessionHold() && this.bpmService.canApprove(this.request(), this.role()));
  /** Enviar borrador: bandeja exige iniciar el proceso; el wizard puede forzar el envío. */
  canSubmitDraft = computed(() => {
    const req = this.request();
    if (!this.bpmService.canSubmitDraft(req)) return false;
    if (this.allowDraftSubmit()) return true;
    return this.bpmService.canInitiateProcess(req?.processSlug, this.role());
  });
  canAct = computed(() => this.canSubmitDraft() || this.canApprove());

  canReviewContent = computed(() => this.bpmService.canReviewContent(this.request(), this.role()));

  /** El paso BPM exige adjuntar documento (configurable en status-flow). */
  stepRequiresDocument = computed(() =>
    this.isDraft() ? false : this.bpmService.stepRequiresDocument(this.request())
  );

  /** Paso de pago / reembolso según BPM. */
  isPaymentStep = computed(() =>
    this.isDraft() ? false : this.bpmService.isPaymentStep(this.request())
  );

  /** Destino: DRAFT → paso inicial; resto → siguiente. */
  nextStatusSlug = computed(() => this.bpmService.submitTargetStatusSlug(this.request()));

  documentTypeName = computed(() =>
    this.bpmService.stepDocumentTypeName(this.request(), 'Documento')
  );

  documentTypeSlug = computed(() => this.bpmService.stepDocumentTypeSlug(this.request()));

  requiredRoleLabel = computed(() => {
    const key = this.bpmService.requiredRole(this.request());
    return key || null;
  });

  reviewRoleLabel = computed(() => this.bpmService.contentReviewRole(this.request()));

  /** Abrir modal si el paso pide documento o es pago (no aplica a DRAFT). */
  needsApprovalModal = computed(() => this.stepRequiresDocument() || this.isPaymentStep());

  approveActionLabel = computed(() => {
    if (this.isDraft()) return 'Enviar solicitud';
    if (this.nextStatusSlug() === 'REIMBURSED') return 'Registrar reembolso';
    if (this.isPaymentStep()) return 'Registrar pago';
    if (this.stepRequiresDocument()) return `Aprobar con ${this.documentTypeName()}`;
    return 'Aprobar';
  });

  /** Hint de qué pide el paso actual (solo lectura BPM). */
  stepHint = computed(() => {
    if (this.sessionHold()) {
      return 'Este paso se aprueba en la sesión de comisión. La bandeja no puede cerrarlo mientras la reunión está abierta.';
    }
    const req = this.request();
    if (this.bpmService.hasBehindGap(req)) {
      const label = this.bpmService.pendingGapLabel(req) || 'anterior';
      return `Hay un paso pendiente (${label}) porque el flujo BPM cambió. Al aprobar se rebobinará a ese paso.`;
    }
    if (this.isPaymentStep()) {
      return this.nextStatusSlug() === 'REIMBURSED'
        ? 'Este paso requiere registrar el reembolso y el comprobante.'
        : 'Este paso requiere registrar el pago y el comprobante.';
    }
    if (this.stepRequiresDocument()) {
      return `Este paso requiere adjuntar: ${this.documentTypeName()}.`;
    }
    return null;
  });

  /** Solo botones si el rol activo tiene privilegio BPM en este paso. */
  showActions = computed(() => this.canAct() || !!this.sessionHold());

  showRejectButton = computed(() =>
    this.showReject() && this.canApprove() && !this.isDraft()
  );

  canConfirmApprove = computed(() =>
    this.canAct() && !this.isProcessing() && !this.blockedReason()
  );

  onApproveClick(): void {
    if (!this.canConfirmApprove()) return;
    if (this.needsApprovalModal()) {
      this.openModal();
      return;
    }
    this.approve.emit();
  }

  onRejectClick(): void {
    if (this.isProcessing() || !this.canApprove()) return;
    this.reject.emit();
  }

  onModalConfirmed(payload: StepApprovalPayload): void {
    this.confirmed.emit(payload);
  }

  openModal(): void {
    this.approvalModal?.open();
  }

  closeModal(): void {
    this.approvalModal?.forceClose();
  }
}
